/**
 * Simulation events to audio cues (Phase 8), as pure data: the engine decides whether and how
 * each request sounds. Shots from one trigger pull (a shotgun's pellets) collapse to one cue;
 * beams and vents are loops keyed by seat, refreshed while their events keep arriving.
 */
import type { MatchEvent, Slot } from '@steelwake/shared/combat';
import type { Vec3 } from '@steelwake/shared/sim';
import { AUDIO_BANK, shotCueFor } from '@steelwake/shared/data';

export interface CueRequest {
  cue: string;
  /** Where it sounds; null for a cue heard as-is (own damage, round flow, stings). */
  position: Vec3 | null;
  /** Loops: the source the voice belongs to; a request refreshes it, `stop` ends it. */
  loopKey?: string;
  stop?: boolean;
  /** Ducks the music under a sting or a blast. */
  duck?: number;
}

export interface DispatchContext {
  mySlot: Slot;
  positionOf(slot: Slot): Vec3;
}

/** Explosions this wide are the deep, low kind (ultimates, artillery). */
const DEEP_EXPLOSION_RADIUS = 5;

export function cuesForEvents(events: readonly MatchEvent[], ctx: DispatchContext): CueRequest[] {
  const out: CueRequest[] = [];
  const shotsSeen = new Set<string>();
  for (const e of events) {
    switch (e.t) {
      case 'shot': {
        // Pellets of one pull share the batch: one report, not eight.
        const key = `${String(e.slot)}:${e.weapon}`;
        if (shotsSeen.has(key)) break;
        shotsSeen.add(key);
        out.push({ cue: shotCueFor(e.weapon).id, position: e.origin });
        if (e.hit === null && e.blocked) out.push({ cue: 'impact.ground', position: e.end });
        break;
      }
      case 'projectileSpawn':
        out.push({ cue: shotCueFor(e.weapon).id, position: e.position });
        break;
      case 'projectileImpact':
        if (e.hit === null) out.push({ cue: 'impact.ground', position: e.position });
        break;
      case 'beam':
        out.push({
          cue: shotCueFor(e.weapon).id,
          position: e.origin,
          loopKey: `beam:${String(e.slot)}`,
        });
        break;
      case 'explosion':
        out.push({
          cue: e.radius >= DEEP_EXPLOSION_RADIUS ? 'explosion.deep' : 'explosion',
          position: e.position,
          duck: 0.35,
        });
        break;
      case 'hit':
        if (e.kind === 'hazard') {
          if (e.target === ctx.mySlot) out.push({ cue: 'hazard', position: null });
          break;
        }
        if (e.kind === 'burn' || e.kind === 'drain' || e.kind === 'suddenDeath') break;
        out.push({ cue: 'impact.mech', position: e.position });
        if (e.target === ctx.mySlot) out.push({ cue: 'damage.taken', position: null });
        break;
      case 'blocked':
        out.push({ cue: 'blocked', position: e.position });
        break;
      case 'parry':
        out.push({ cue: 'parry', position: e.position });
        break;
      case 'kill':
        out.push({ cue: 'explosion.deep', position: ctx.positionOf(e.victim), duck: 0.5 });
        if (e.killer === ctx.mySlot) out.push({ cue: 'kill', position: null });
        break;
      case 'melee':
        out.push({ cue: e.hit === null ? 'melee.swing' : 'melee.hit', position: e.position });
        break;
      case 'shieldBroken':
        out.push({ cue: 'shield.break', position: ctx.positionOf(e.slot) });
        break;
      case 'overheat':
        out.push({ cue: 'overheat', position: ctx.positionOf(e.slot) });
        break;
      case 'vent':
        out.push({
          cue: 'vent.loop',
          position: ctx.positionOf(e.slot),
          loopKey: `vent:${String(e.slot)}`,
          ...(e.on ? {} : { stop: true }),
        });
        break;
      case 'ability':
        out.push({ cue: e.index === 1 ? 'ultimate' : 'ability', position: e.position });
        break;
      case 'reload':
        out.push({ cue: 'reload', position: ctx.positionOf(e.slot) });
        break;
      case 'pickup':
        out.push({
          cue: e.kind === 'shieldCell' ? 'pickup.shield' : 'pickup.heatsink',
          position: ctx.positionOf(e.slot),
        });
        break;
      case 'padLaunch':
        out.push({ cue: 'pad.launch', position: ctx.positionOf(e.slot) });
        break;
      case 'ultCharged':
        if (e.slot === ctx.mySlot) out.push({ cue: 'ult.charged', position: null });
        break;
      case 'roundStart':
        out.push({ cue: 'round.start', position: null, duck: 0.5 });
        break;
      case 'live':
        out.push({ cue: 'round.live', position: null });
        break;
      case 'roundEnd':
        if (e.winner !== null) {
          out.push({
            cue: e.winner === ctx.mySlot ? 'round.won' : 'round.lost',
            position: null,
            duck: 0.6,
          });
        }
        break;
      case 'suddenDeath':
        out.push({ cue: 'sudden-death', position: null, duck: 0.4 });
        break;
      case 'matchEnd':
        if (e.winner !== null) {
          out.push({
            cue: e.winner === ctx.mySlot ? 'match.won' : 'match.lost',
            position: null,
            duck: 0.8,
          });
        }
        break;
      default:
        break;
    }
  }
  return out;
}

/** Cue ids a match needs loaded before it starts (everything but the menu music). */
export function matchCueIds(): string[] {
  return AUDIO_BANK.cues.map((c) => c.id).filter((id) => id !== AUDIO_BANK.music.menu);
}
